import { membersTotalPremiums } from '../../../helpers';
import { checkCoverageTypeChange } from './memberHelpers';

/**
 * Calculate new premium after member removal
 * @param {Array} remainingMembers - Members that will remain on policy
 * @param {Object} policy - Current policy object
 * @param {Array} products - Available products for premium rates
 * @returns {Object} - Premium calculation details
 */
export const calculateNewPremium = (remainingMembers, policy, products) => {
  if (!remainingMembers || remainingMembers.length === 0) {
    return {
      oldTotal: 0,
      newTotal: 0,
      savings: 0,
      coverageTypeChange: null
    };
  }

  // Get current total premium from policy
  const oldTotal = policy.total_premium_amount_in_cents
    ? policy.total_premium_amount_in_cents / 100
    : 0;

  // Check if coverage type will change
  const coverageCheck = checkCoverageTypeChange(remainingMembers);

  // Calculate new total using helper function
  const newTotal = membersTotalPremiums(remainingMembers);

  // Calculate savings
  const savings = oldTotal - newTotal;

  return {
    oldTotal: oldTotal,
    newTotal: newTotal,
    savings: savings,
    coverageTypeChange: coverageCheck.willChange ? {
      from: policy.quotation.additional_details.cover_type,
      to: coverageCheck.newCoverageType
    } : null
  };
};

/**
 * Build quotation payload for removed members
 * @param {Object} policy - Current policy
 * @param {Array} remainingMembers - Members to keep
 * @param {Object} coverage - Coverage type details
 * @param {Array} addedMembers - Newly added members (to set 150 day waiting period)
 * @returns {Object} - Quotation payload for API
 */
export const buildRemovalQuotation = (policy, remainingMembers, coverage, addedMembers = []) => {
  const coverageType = coverage && coverage.to ? coverage.to : policy.quotation.additional_details.cover_type;

  // Create set of added member UUIDs for quick lookup
  const addedMemberUuids = new Set(addedMembers.map(m => m.uuid));

  // Build policy schedule entries from remaining members
  // All fields should be at top level, not nested under 'dependant'
  const policy_schedule_entries = remainingMembers.map(member => {
    // Check if this is a newly added member
    const isNewMember = addedMemberUuids.has(member.uuid);

    // If new member, set waiting_period_in_days to 150 for all their benefits
    const benefits = isNewMember
      ? (member.benefits || []).map(benefit => ({
          ...benefit,
          waiting_period_in_days: 150
        }))
      : member.benefits || [];

    return {
      guid: member.guid,
      first_name: member.first_name,
      middle_name: member.middle_name || '',
      last_name: member.last_name,
      msisdn: member.msisdn || member.mobile_number,
      date_of_birth: member.date_of_birth,
      gender: member.gender,
      national_id: member.national_id || member.id_number || 'N/A',
      passport: member.passport || 'N/A',
      postal_address: member.postal_address || '',
      relationship: member.relationship,
      residential_address: member.residential_address || '',
      type: member.type || 'Nuclear',
      premium_amount_in_cents: member.premium_amount_in_cents || 0,
      extended_premium: member.extended_premium || 0,
      add_on_premium: member.add_on_premium || 0,
      benefits: benefits,
      uuid: member.uuid,
      additional_details: member.additional_details || {}
    };
  });

  return {
    product_guid: policy.product_guid,
    product_name: policy.product_name,
    product_code: policy.product_code,
    branch: policy.branch || 'string',
    customer_guid: policy.customer_guid,
    partner: policy.partner,
    lapse_period: policy.lapse_period || 12,
    lapse_period_granularity: policy.lapse_period_granularity || 'month',
    lapse_period_cardinality: policy.lapse_period_cardinality || 1,
    premium_granularity: policy.premium_granularity || 'month',
    premium_cardinality: policy.premium_cardinality || 1,
    policy_schedule_entries: policy_schedule_entries,
    additional_details: {
      ...policy.quotation.additional_details,
      cover_type: coverageType
    }
  };
};
